import _ from 'lodash';
import React from 'react';

import {default as Modal, withModal} from 'components/Modal';

import {
  MODALS,
  KYC_SECTION_TYPES,
  selectAgreementUser,
  verifyKyc,
  fetchKycs,
  verifyPOID,
  verifyAddress,
  confirmCurrentAddress,
  deleteKycDocument,
  fetchBooking
} from 'ducks/bookings';
import {KycNavigation, KycSidebar, KycMainContent} from './components';
import {
  MODALS as BASE_MODALS,
  closeModal,
  storeDataInModal
} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import './styles.css';

class KycModal extends React.Component<any> {
  state = {
    selectedSectionType: KYC_SECTION_TYPES.POID
  };

  componentDidMount() {
    this.props.fetchKycDetails(this.props.data.kycId);
  }

  sidebarSectionClickHandler = sectionType => {
    const {locationRef} = this.props.data;
    let permission = PERMISSIONS.VIEW_KYC_DETAILED_VIEW;

    if (sectionType === KYC_SECTION_TYPES.VISA)
      permission = PERMISSIONS.VIEW_VISA_KYC_SECTION;

    if (sectionType === KYC_SECTION_TYPES.POA)
      permission = PERMISSIONS.VIEW_POA_KYC_SECTION;

    this.props.requiresPermission({locationRef, permission}, () =>
      this.setState({selectedSectionType: sectionType})
    );
  };

  verifyButtonHandler = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VERIFY_KYC
      },
      () => {
        const {selectedSectionType} = this.state;

        if (selectedSectionType === KYC_SECTION_TYPES.POID) {
          this.openVerifyPOIDModal();
        }
        if (selectedSectionType === KYC_SECTION_TYPES.VISA) {
          this.props.openVerifyVisaModal();
        }
        if (selectedSectionType === KYC_SECTION_TYPES.POA) {
          this.openVerifyAddressModal();
        }
      }
    );

  addAddressHandler = () => {
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.EDIT_KYC
      },
      () => {
        const agreementUserId = this.props.data.kyc.agreementUser.id;

        this.props.openAddAddressHistoryModal({agreementUserId});
      }
    );
  };

  verifyKycConfirmAction = () => {
    const {data: {bookingId, kycId, locationRef}, fetchKycDetails} = this.props;

    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () => fetchKycDetails(kycId),
      () => fetchKycs({bookingId}),
      () => fetchBooking({bookingId, locationRef})
    ];

    const failureExtraActions = [
      ({errors}) =>
        this.props.storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {
            errors,
            submitting: false
          }
        })
    ];

    this.props.verifyKyc(
      {bookingId, kycId},
      successExtraActions,
      failureExtraActions
    );
  };

  openVerifyKycModal = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VERIFY_KYC
      },
      () =>
        this.props.openConfirmationModal({
          heading: 'Verify KYC',
          question: 'Are you sure you want to verify KYC?',
          confirmAction: this.verifyKycConfirmAction
        })
    );

  verifyPOIConfirmAction = () => {
    const {data: {bookingId, kycId}, fetchKycDetails} = this.props;

    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () => fetchKycDetails(kycId)
    ];
    const failureExtraActions = [
      ({errors}) =>
        this.props.storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {
            errors,
            submitting: false
          }
        })
    ];

    this.props.verifyPOID(
      {bookingId, kycId},
      successExtraActions,
      failureExtraActions
    );
  };

  openVerifyPOIDModal = () =>
    this.props.openConfirmationModal({
      heading: 'Verify Proof of Identification',
      question: 'Are you sure you want to verify Proof of Identification?',
      confirmAction: this.verifyPOIConfirmAction
    });

  verifyAddressConfirmAction = addressHistoryId => {
    const {data: {bookingId, kycId}, fetchKycDetails} = this.props;

    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () => fetchKycDetails(kycId)
    ];

    const failureExtraActions = [
      ({errors}) =>
        this.props.storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {
            errors,
            submitting: false
          }
        })
    ];

    this.props.verifyAddress(
      {bookingId, addressHistoryId, kycId},
      successExtraActions,
      failureExtraActions
    );
  };

  getCurrentAddress = () => {
    const {data: {kyc}} = this.props;
    const addressHistory = _.get(kyc, 'addressHistory', []);

    return _.find(addressHistory, address => address.current);
  };

  openVerifyAddressModal = () =>
    this.props.openConfirmationModal({
      heading: 'Verify Proof of Address',
      question: 'Are you sure you want to verify Proof of Address?',
      confirmAction: () =>
        this.verifyAddressConfirmAction(this.getCurrentAddress().id)
    });

  openConfirmDeletionModal = ({confirmAction, documentName}) => {
    this.props.openConfirmationModal({
      heading: 'Delete document',
      question: `Are you sure you want to permanently delete ${documentName}?`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Delete',
      confirmAction: confirmAction
    });
  };

  currentAddressConfirmAction = addressHistoryId => {
    const {
      data: {kycId, kyc: {agreementUser: {id}}},
      fetchKycDetails
    } = this.props;
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () => fetchKycDetails(kycId)
    ];
    const failureExtraActions = [
      ({errors}) =>
        this.props.storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {
            errors,
            submitting: false
          }
        })
    ];

    this.props.confirmCurrentAddress(
      {agreementUserId: id, addressHistoryId},
      successExtraActions,
      failureExtraActions
    );
  };

  confirmCurrentAddressHandler = addressHistoryId =>
    this.props.openConfirmationModal({
      heading: 'Confirm current address',
      question: 'Are you sure you want confirm the Address as CURRENT?',
      confirmAction: () => this.currentAddressConfirmAction(addressHistoryId)
    });

  deleteDocument = ({kycDocumentName, kycDocumentId}) => {
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.EDIT_KYC
      },
      () => {
        const {data: {kycId}, fetchKycDetails} = this.props;

        const deleteDocumentExtraActions = [
          () => closeModal(BASE_MODALS.CONFIRM),
          () => fetchKycDetails(kycId)
        ];

        const confirmAction = () =>
          this.props.deleteKycDocument(
            {kycDocumentId, kyc: kycId},
            deleteDocumentExtraActions
          );

        this.openConfirmDeletionModal({
          confirmAction,
          documentName: kycDocumentName
        });
      }
    );
  };

  render() {
    const {
      closeModal,
      openKycDocumentUploadModal,
      openRequestKycDocumentModal,
      data: {kyc, agreementUser, bookingId}
    } = this.props;
    const status = _.get(kyc, 'status');
    const addressHistory = _.get(kyc, 'addressHistory');
    const kycSectionsIds = _.get(kyc, 'sections');

    const selectedSection = _.get(
      this.props.data,
      this.state.selectedSectionType
    );
    const verifications = _.get(selectedSection, 'verificationHistory', []);
    const documentRequests = _.get(selectedSection, 'documentRequests', []);

    return (
      <Modal
        show={!_.isNil(kyc)}
        onClose={closeModal}
        type={Modal.LARGE}
        className="KycModal">
        <Modal.Header heading="KYC" />
        <Modal.Body>
          <div className="KycContainer">
            <KycNavigation
              status={status}
              agreementUser={agreementUser}
              openVerifyKycModal={this.openVerifyKycModal}
              openKycDocumentUploadModal={openKycDocumentUploadModal}
              openRequestKycDocumentModal={() =>
                openRequestKycDocumentModal({
                  bookingId,
                  kycSectionsIds,
                  selectedSectionType: this.state.selectedSectionType
                })
              }
            />
            <KycSidebar
              selectedSectionType={this.state.selectedSectionType}
              sectionClickHandler={this.sidebarSectionClickHandler}
            />
            {selectedSection && (
              <KycMainContent
                addressHistory={addressHistory}
                verifications={verifications}
                selectedSection={selectedSection}
                currentAddress={this.getCurrentAddress()}
                verifyButtonHandler={this.verifyButtonHandler}
                addAddressHandler={this.addAddressHandler}
                confirmCurrentAddressHandler={this.confirmCurrentAddressHandler}
                deleteDocument={this.deleteDocument}
                documentRequests={documentRequests}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}

const mapStateToModalData = (state, data) => {
  const {kyc} = data;

  const selectedAgreementUser =
    selectAgreementUser(state, _.get(kyc, 'agreementUser.id')) || {};

  return {agreementUser: selectedAgreementUser};
};

const mapDispatchToProps = {
  storeDataInModal,
  verifyKyc,
  fetchKycs,
  verifyPOID,
  verifyAddress,
  confirmCurrentAddress,
  deleteKycDocument,
  requiresPermission
};

export default withModal(MODALS.KYC, mapStateToModalData, mapDispatchToProps)(
  KycModal
);
